import { createElement } from "react";
import type { ReactElement } from "react";

import { GLYPHS } from "./glyphs";
import type { GlyphShape } from "./glyphs";

/** Every glyph takes the same one prop. */
export interface IconGlyphProps {
  readonly className?: string | undefined;
}

/** The name of a glyph the design system ships. */
export type IconName = keyof typeof GLYPHS;

export type IconGlyph = (props: IconGlyphProps) => ReactElement;

function shape(entry: GlyphShape, index: number): ReactElement {
  const { tag, ...attributes } = entry;
  return createElement(tag, { key: index, ...attributes });
}

/**
 * One glyph component built from its shapes.
 *
 * Inline SVG, never an icon font and never an `<img>` (F-UIX-006), so the static server needs no
 * extra source in its CSP. The `<svg>` carries the only paint attributes: `stroke="currentColor"`
 * and `fill="none"`, so a glyph takes the colour of the text around it and there is no second
 * attribute where a literal colour could hide. `1em` sizes it from that text; the caller's class
 * picks the step of the type scale.
 */
function glyph(shapes: readonly GlyphShape[]): IconGlyph {
  return function Glyph({ className }: IconGlyphProps): ReactElement {
    return createElement(
      "svg",
      {
        viewBox: "0 0 24 24",
        width: "1em",
        height: "1em",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: 1.5,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        "aria-hidden": "true",
        focusable: "false",
        className,
      },
      shapes.map(shape),
    );
  };
}

/** Every glyph of the redesign mockup, by name, in the mockup's order. */
export const ICONS = Object.fromEntries(
  Object.entries(GLYPHS).map(([name, shapes]) => [name, glyph(shapes)]),
) as Readonly<Record<IconName, IconGlyph>>;
